import React from "react";
import { Box, Link } from "@mui/material";
import { colors, fonts, gradients, layout } from "../../theme/tokens";

const heroImage =
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1100&auto=format&fit=crop";

const inlineStats = [
  { value: "24", suffix: "+", label: "Years Experience" },
  { value: "8", suffix: "", label: "Service Pillars" },
  { value: "100", suffix: "+", label: "Clients Served" },
];

function HomeIntro() {
  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        pt: { xs: "120px", md: "150px" },
        pb: { xs: "60px", md: "90px" },
        px: { xs: "20px", md: "28px" },
        "@keyframes heroUp": {
          from: { transform: "translateY(26px)", opacity: 0 },
          to: { transform: "translateY(0)", opacity: 1 },
        },
        "@keyframes heroFade": {
          from: { transform: "translateY(16px)", opacity: 0 },
          to: { transform: "translateY(0)", opacity: 1 },
        },
        "@keyframes floatY": {
          "0%,100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(-14px)" },
        },
        "@keyframes floatY2": {
          "0%,100%": { transform: "translateY(0)" },
          "50%": { transform: "translateY(10px)" },
        },
        "@keyframes glowPulse": {
          "0%,100%": { opacity: 0.5, transform: "scale(1)" },
          "50%": { opacity: 0.85, transform: "scale(1.08)" },
        },
      }}
    >
      {/* Decorative glows */}
      <Box
        sx={{
          position: "absolute",
          top: "-160px",
          right: "-120px",
          width: "560px",
          height: "560px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(246,166,35,.20),transparent 65%)",
          animation: "glowPulse 7s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-180px",
          left: "-140px",
          width: "520px",
          height: "520px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(44,108,168,.16),transparent 65%)",
          animation: "glowPulse 9s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      {/* Grid backdrop */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(20,38,59,.035) 1px,transparent 1px),linear-gradient(90deg,rgba(20,38,59,.035) 1px,transparent 1px)",
          backgroundSize: "54px 54px",
          maskImage:
            "radial-gradient(ellipse 80% 60% at 50% 40%,#000,transparent)",
          WebkitMaskImage:
            "radial-gradient(ellipse 80% 60% at 50% 40%,#000,transparent)",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          maxWidth: layout.maxWidth,
          mx: "auto",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.05fr .95fr" },
          gap: { xs: "40px", md: "60px" },
          alignItems: "center",
        }}
      >
        {/* Left column */}
        <Box sx={{ minWidth: 0 }}>
          {/* Badge */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              background: "#fff",
              border: `1px solid ${colors.line}`,
              p: "8px 16px",
              borderRadius: "40px",
              boxShadow: "0 6px 20px -12px rgba(20,38,59,.3)",
              animation: "heroUp .7s cubic-bezier(.2,.7,.2,1) both",
            }}
          >
            <Box
              sx={{
                width: "7px",
                height: "7px",
                borderRadius: "50%",
                background: colors.success,
                boxShadow: "0 0 0 4px rgba(34,165,91,.18)",
              }}
            />
            <Box
              sx={{
                fontFamily: fonts.display,
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: ".18em",
                color: colors.steelBlue,
              }}
            >
              TRUSTED CONSULTING PARTNER
            </Box>
          </Box>

          {/* Headline */}
          <Box
            component="h1"
            sx={{
              fontFamily: fonts.display,
              fontWeight: 700,
              fontSize: "clamp(40px,5.4vw,68px)",
              lineHeight: 1.02,
              letterSpacing: "-.02em",
              color: colors.navyInk,
              mt: "24px",
              mb: 0,
              animation: "heroUp .75s .08s cubic-bezier(.2,.7,.2,1) both",
            }}
          >
            We build financial
            <br />
            clarity &{" "}
            <Box
              component="span"
              sx={{
                position: "relative",
                whiteSpace: "nowrap",
                color: colors.brandNavy,
              }}
            >
              strategic
              <Box
                component="svg"
                viewBox="0 0 300 18"
                preserveAspectRatio="none"
                sx={{
                  position: "absolute",
                  left: 0,
                  bottom: "-6px",
                  width: "100%",
                  height: "14px",
                }}
              >
                <path
                  d="M2 12 C 70 4, 230 4, 298 11"
                  fill="none"
                  stroke={colors.amber}
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </Box>
            </Box>{" "}
            growth.
          </Box>

          {/* Subtitle */}
          <Box
            sx={{
              fontFamily: fonts.body,
              fontSize: "18px",
              lineHeight: 1.65,
              color: colors.mutedText,
              maxWidth: "520px",
              mt: "26px",
              animation: "heroUp .8s .16s cubic-bezier(.2,.7,.2,1) both",
            }}
          >
            Argyle Consulting delivers expert solutions across strategy,
            corporate finance, advisory, human resource, risk & compliance,
            audit, technology, and property, serving businesses in Nigeria and
            beyond.
          </Box>

          {/* CTAs */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "14px",
              mt: "34px",
              animation: "heroUp .85s .24s cubic-bezier(.2,.7,.2,1) both",
            }}
          >
            <Link
              href="/contact-us"
              underline="none"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                background: gradients.amber,
                color: "#fff",
                fontFamily: fonts.body,
                fontWeight: 700,
                fontSize: "15px",
                p: "15px 30px",
                borderRadius: "44px",
                boxShadow: "0 14px 32px -12px rgba(242,121,28,.7)",
                transition: "transform .25s ease",
                "&:hover": { transform: "translateY(-1px)" },
              }}
            >
              Book a Consultation{" "}
              <Box component="span" sx={{ fontSize: "18px" }}>
                →
              </Box>
            </Link>
            <Link
              href="/services"
              underline="none"
              sx={{
                background: "#fff",
                color: colors.brandNavy,
                fontFamily: fonts.body,
                fontWeight: 700,
                fontSize: "15px",
                p: "15px 30px",
                borderRadius: "44px",
                border: `1px solid ${colors.line}`,
                transition: "border-color .25s ease",
                "&:hover": { borderColor: colors.brandNavy },
              }}
            >
              Our Services
            </Link>
          </Box>

          {/* Inline stats */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "22px",
              mt: "40px",
              animation: "heroFade 1s .5s both",
            }}
          >
            {inlineStats.map((stat, i) => (
              <React.Fragment key={stat.label}>
                {i > 0 && (
                  <Box
                    sx={{
                      width: "1px",
                      height: "36px",
                      background: colors.line,
                    }}
                  />
                )}
                <Box>
                  <Box
                    sx={{
                      fontFamily: fonts.display,
                      fontWeight: 700,
                      fontSize: "26px",
                      color: colors.navyInk,
                    }}
                  >
                    {stat.value}
                    <Box component="span" sx={{ color: colors.amber }}>
                      {stat.suffix}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      fontFamily: fonts.body,
                      fontSize: "12.5px",
                      color: "#7A8896",
                      fontWeight: 500,
                    }}
                  >
                    {stat.label}
                  </Box>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>

        {/* Right column — image + floating cards */}
        <Box
          sx={{
            position: "relative",
            animation: "heroFade 1s .2s both",
            display: { xs: "none", sm: "block" },
          }}
        >
          <Box
            sx={{
              position: "relative",
              borderRadius: "26px",
              overflow: "hidden",
              boxShadow: "0 40px 80px -36px rgba(14,27,42,.5)",
              transform: "rotate(1.4deg)",
            }}
          >
            <Box
              component="img"
              src={heroImage}
              alt="City skyline"
              loading="lazy"
              sx={{
                width: "100%",
                height: { xs: "400px", md: "520px" },
                objectFit: "cover",
                display: "block",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg,transparent 40%,rgba(14,27,42,.55))",
              }}
            />
          </Box>

          {/* Floating growth card */}
          <Box
            sx={{
              position: "absolute",
              top: "24px",
              left: "-26px",
              background: "#fff",
              borderRadius: "18px",
              p: "16px 18px",
              boxShadow: "0 24px 48px -22px rgba(14,27,42,.45)",
              animation: "floatY 6s ease-in-out infinite",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Box
              sx={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: gradients.navy,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M4 19V5M4 19h16M8 16V9M12 16V6M16 16v-4M20 16v-7"
                  stroke={colors.amberDot}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </Box>
            </Box>
            <Box>
              <Box
                sx={{
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: "18px",
                  color: colors.navyInk,
                }}
              >
                +38% growth
              </Box>
              <Box
                sx={{
                  fontFamily: fonts.body,
                  fontSize: "12px",
                  color: "#7A8896",
                }}
              >
                avg. performance lift
              </Box>
            </Box>
          </Box>

          {/* Floating advisors card */}
          <Box
            sx={{
              position: "absolute",
              bottom: "30px",
              right: "-22px",
              background: "rgba(255,255,255,.92)",
              backdropFilter: "blur(8px)",
              borderRadius: "18px",
              p: "16px 18px",
              boxShadow: "0 24px 48px -22px rgba(14,27,42,.45)",
              animation: "floatY2 7s ease-in-out infinite",
              maxWidth: "200px",
            }}
          >
            <Box sx={{ display: "flex", mb: "8px" }}>
              <Box
                sx={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#2C6CA8,#4A90C2)",
                  border: "2px solid #fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontFamily: fonts.body,
                  fontSize: "11px",
                  fontWeight: 700,
                }}
              >
                SN
              </Box>
              <Box
                sx={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  ml: "-8px",
                  background: gradients.amber,
                  border: "2px solid #fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontFamily: fonts.body,
                  fontSize: "11px",
                  fontWeight: 700,
                }}
              >
                AE
              </Box>
            </Box>
            <Box
              sx={{
                fontFamily: fonts.body,
                fontSize: "12.5px",
                color: colors.mutedText,
                lineHeight: 1.4,
              }}
            >
              Senior advisors with{" "}
              <Box component="b" sx={{ color: colors.brandNavy }}>
                45+ yrs
              </Box>{" "}
              combined leadership.
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default HomeIntro;
